import { useState, useEffect, useCallback, useRef } from "react";
import { BridgeEvent, NativeBridgeAvailable } from "@unu/ui-enums";
import { sendEvent } from "@unu/ui-common";
import { registerEvents } from "@unu/web-native-bridge";
import {
  captureHealthException,
  addHealthSyncBreadcrumb,
  setNativeAppVersion,
} from "@/services/application-monitoring-adapter";
import type { SDKDataType } from "@/health/healthDataTypes";

/** Dedup set — report each native error code at most once per page session */
const reportedHealthErrorCodes = new Set<string>();

export type { SDKDataType } from "@/health/healthDataTypes";

/**
 * Health record from native platform
 */
export interface HealthRecord {
  platform: "healthkit" | "health_connect";
  sourceId: string;
  dataType: SDKDataType;
  value: number;
  unit: string;
  timestamp: string;
  endTimestamp?: string;
  source: string;
  metadata?: Record<string, unknown>;
}

/**
 * Permission status for a data type
 */
export interface DataTypePermission {
  type: SDKDataType;
  read: "not_determined" | "authorized" | "denied" | "restricted" | "limited";
  write?: "not_determined" | "authorized" | "denied" | "restricted" | "limited";
}

/**
 * Health permissions state
 */
export interface HealthPermissions {
  dataTypes: DataTypePermission[];
  allGranted: boolean;
  hasAnyGranted: boolean;
  hasDenied: boolean;
  lastChecked: string;
}

/**
 * Health error
 */
export interface HealthError {
  code: string;
  message: string;
  action?: string;
}

export const PlatformType = {
  HEALTHKIT: "healthkit",
  HEALTH_CONNECT: "health_connect",
} as const;

export type Platform = (typeof PlatformType)[keyof typeof PlatformType];

/**
 * Health platform status
 */
export interface HealthStatus {
  platform: Platform;
  isConnected: boolean;
  isAvailable: boolean;
  isLoading: boolean;
  isFetching: boolean;
  permissions: HealthPermissions | null;
  error: HealthError | null;
  isSamsungHealthInstalled: boolean;
  nativeAppVersion: string | null;
  nativeBuildNumber: string | null;
  binahNativeSdkVersion: string | null;
}

/**
 * Options for fetching health data
 */
export interface FetchHealthDataOptions {
  dataTypes?: SDKDataType[];
  startDate?: Date;
  endDate?: Date;
  limit?: number;
}

/**
 * Hook return type
 */
export interface UseNativeHealthSDKResult {
  // Platform state
  status: HealthStatus | null;
  isAvailable: boolean;
  isConnected: boolean;
  hasAnyPermissions: boolean;
  isLoading: boolean;
  permissions: HealthPermissions | null;
  error: HealthError | null;
  isSamsungHealthInstalled: boolean;

  // Data state
  records: HealthRecord[];
  isFetching: boolean;

  // Actions
  requestPermissions: (dataTypes?: SDKDataType[]) => void;
  fetchHealthData: (options?: FetchHealthDataOptions) => void;
  refreshStatus: () => void;
  openHealthConnectSettings: () => void;
  openAppPermissionSettings: () => void;
  revokeAllPermissions: () => void;

  // Helpers
  getRecordsByType: (dataType: SDKDataType) => HealthRecord[];
  getLatestRecord: (dataType: SDKDataType) => HealthRecord | null;
  clearRecords: () => void;
}

/**
 * useNativeHealthSDK
 *
 * Hook for web app to interact with native health SDK (HealthKit/Health Connect)
 * via the webview bridge.
 *
 * @example
 * ```tsx
 * const {
 *   isConnected,
 *   isAvailable,
 *   permissions,
 *   records,
 *   isFetching,
 *   requestPermissions,
 *   fetchHealthData,
 * } = useNativeHealthSDK();
 *
 * // Request permissions (snake_case SDK types for native bridge)
 * requestPermissions(['heart_rate', 'steps', 'weight']);
 *
 * // Fetch data
 * fetchHealthData({
 *   dataTypes: ['heart_rate', 'steps'],
 *   startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
 *   endDate: new Date(),
 * });
 * ```
 */
export function useNativeHealthSDK(): UseNativeHealthSDKResult {
  const [status, setStatus] = useState<HealthStatus | null>(null);
  const [records, setRecords] = useState<HealthRecord[]>([]);
  const [error, setError] = useState<HealthError | null>(null);
  const prevStatusRef = useRef<{ isAvailable: boolean; isConnected: boolean }>({
    isAvailable: false,
    isConnected: false,
  });
  const isFirstStatusUpdateRef = useRef(true);

  const handleStatusUpdate = useCallback((data: any) => {
    if (data.nativeAppVersion) {
      setNativeAppVersion(data.nativeAppVersion);
    }

    setStatus({
      platform: data.platform,
      isConnected: data.isConnected ?? false,
      isAvailable: data.isAvailable ?? false,
      isLoading: data.isLoading ?? false,
      isFetching: data.isFetching ?? false,
      permissions: data.permissions ?? null,
      error: data.error ?? null,
      isSamsungHealthInstalled: data.isSamsungHealthInstalled ?? false,
      nativeAppVersion: data.nativeAppVersion ?? null,
      nativeBuildNumber: data.nativeBuildNumber ?? null,
      binahNativeSdkVersion: data.binahNativeSdkVersion ?? null,
    });

    const newAvailable = data.isAvailable ?? false;
    const newConnected = data.isConnected ?? false;
    const prev = prevStatusRef.current;

    const isFirstUpdate = isFirstStatusUpdateRef.current;

    if (
      isFirstUpdate ||
      newAvailable !== prev.isAvailable ||
      newConnected !== prev.isConnected
    ) {
      addHealthSyncBreadcrumb("native_status_changed", {
        platform: data.platform ?? null,
        isAvailable: newAvailable,
        isConnected: newConnected,
        prevIsAvailable: prev.isAvailable,
        prevIsConnected: prev.isConnected,
        hasError: Boolean(data.error),
        errorCode: data.error?.code ?? null,
        isSamsungHealthInstalled: data.isSamsungHealthInstalled ?? null,
        isFirstUpdate,
      });
      isFirstStatusUpdateRef.current = false;
    }
    prevStatusRef.current = {
      isAvailable: newAvailable,
      isConnected: newConnected,
    };

    setError(data.error ?? null);
  }, []);

  const handleDataReceived = useCallback((data: any) => {
    if (data.records && Array.isArray(data.records)) {
      setRecords(data.records);
    }

    if (data.error) {
      setError(data.error);
    }
  }, []);

  const handlePermissionsResult = useCallback((data: any) => {
    if (data.permissions) {
      setStatus((prev) =>
        prev
          ? {
              ...prev,
              permissions: data.permissions,
              isConnected: data.permissions.hasAnyGranted,
            }
          : null
      );
    }

    addHealthSyncBreadcrumb("native_permissions_result", {
      success: data.success ?? null,
      allGranted: data.permissions?.allGranted ?? null,
      grantedCount:
        data.permissions?.dataTypes?.filter((d: any) => d.read === "authorized")
          .length ?? null,
      totalCount: data.permissions?.dataTypes?.length ?? null,
      hasError: Boolean(data.error),
      errorCode: data.error?.code ?? null,
      requiresManualSetup: data.requiresManualSetup ?? null,
      requiresSettings: data.requiresSettings ?? null,
    });

    setError(data.error ?? null);
  }, []);

  const handleError = useCallback((data: any) => {
    const code = data.code ?? "UNKNOWN_ERROR";

    setError({
      code,
      message: data.message ?? "An unknown error occurred",
      action: data.action,
    });

    if (!reportedHealthErrorCodes.has(code)) {
      reportedHealthErrorCodes.add(code);
      captureHealthException(new Error(`Native health error: ${code}`), {
        phase: "nativeHealthSDK",
        errorCode: code,
        errorMessage: data.message ?? "An unknown error occurred",
        action: data.action ?? null,
        isAvailable: prevStatusRef.current.isAvailable,
        isConnected: prevStatusRef.current.isConnected,
        isSamsungHealthInstalled: data.isSamsungHealthInstalled ?? null,
      });
    }
  }, []);

  useEffect(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    const events = {
      [BridgeEvent.HealthStatusUpdate]: handleStatusUpdate,
      [BridgeEvent.HealthDataReceived]: handleDataReceived,
      [BridgeEvent.HealthPermissionsResult]: handlePermissionsResult,
      [BridgeEvent.HealthError]: handleError,
    };

    return registerEvents(events);
  }, [
    handleStatusUpdate,
    handleDataReceived,
    handlePermissionsResult,
    handleError,
  ]);

  useEffect(() => {
    if (NativeBridgeAvailable) {
      sendEvent({
        name: BridgeEvent.SendData,
        data: {
          name: BridgeEvent.HealthGetStatus,
        },
      });
    }
  }, []);

  const requestPermissions = useCallback((dataTypes?: SDKDataType[]) => {
    if (!NativeBridgeAvailable) {
      setError({
        code: "BRIDGE_UNAVAILABLE",
        message: "Native bridge is not available",
      });
      return;
    }

    addHealthSyncBreadcrumb("native_permissions_requested", {
      dataTypeCount: dataTypes?.length ?? null,
    });

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthRequestPermissions,
        data: { dataTypes },
      },
    });
  }, []);

  const fetchHealthData = useCallback((options?: FetchHealthDataOptions) => {
    if (!NativeBridgeAvailable) {
      setError({
        code: "BRIDGE_UNAVAILABLE",
        message: "Native bridge is not available",
      });
      return;
    }

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthFetchData,
        data: {
          dataTypes: options?.dataTypes,
          startDate: options?.startDate?.toISOString(),
          endDate: options?.endDate?.toISOString(),
          limit: options?.limit,
        },
      },
    });
  }, []);

  const refreshStatus = useCallback(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthGetStatus,
      },
    });
  }, []);

  const openHealthConnectSettings = useCallback(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    addHealthSyncBreadcrumb("native_open_health_connect_settings");
    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthOpenSettings,
      },
    });
  }, []);

  const openAppPermissionSettings = useCallback(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    addHealthSyncBreadcrumb("native_open_app_permission_settings");
    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthOpenAppSettings,
      },
    });
  }, []);

  const revokeAllPermissions = useCallback(() => {
    if (!NativeBridgeAvailable) {
      return;
    }

    addHealthSyncBreadcrumb("native_revoke_all_permissions");
    sendEvent({
      name: BridgeEvent.SendData,
      data: {
        name: BridgeEvent.HealthRevokePermissions,
      },
    });
  }, []);

  const getRecordsByType = useCallback(
    (dataType: SDKDataType): HealthRecord[] => {
      return records.filter((record) => record.dataType === dataType);
    },
    [records]
  );

  const getLatestRecord = useCallback(
    (dataType: SDKDataType): HealthRecord | null => {
      const typeRecords = records
        .filter((record) => record.dataType === dataType)
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

      return typeRecords[0] ?? null;
    },
    [records]
  );

  const clearRecords = useCallback(() => {
    setRecords([]);
    setError(null);
  }, []);

  return {
    // Platform state
    status,
    isAvailable: status?.isAvailable ?? false,
    isConnected:
      status?.isConnected ?? status?.permissions?.hasAnyGranted ?? false,
    hasAnyPermissions: status?.permissions?.hasAnyGranted ?? false,
    isLoading: status?.isLoading ?? false,
    permissions: status?.permissions ?? null,
    error,
    isSamsungHealthInstalled: status?.isSamsungHealthInstalled ?? false,
    // Data state
    records,
    isFetching: status?.isFetching ?? false,
    // Actions
    requestPermissions,
    fetchHealthData,
    refreshStatus,
    openHealthConnectSettings,
    openAppPermissionSettings,
    revokeAllPermissions,
    // Helpers
    getRecordsByType,
    getLatestRecord,
    clearRecords,
  };
}

export default useNativeHealthSDK;
